import type { Data, Fields, RawData } from '../../../types';
import { normalizeCheckboxValue } from './normalizeCheckboxValue';
import { Columns, ColumnType } from '../types';
import { normalizeMultiValue } from './normalizeMultiValue';
import { filterNotEmpty } from '@beeline.com/bp-design-system';

export const normalizeTableData = <T extends string>(columns: Columns<T>, data: RawData[], fields: Fields<T>) =>
  data.map((row) =>
    columns.reduce(
      (acc, column, index) => {
        const curr = row[index];
        switch (column.type) {
          case ColumnType.matchedCheckbox: {
            const field = fields.find((field) => field.key === column.value)!;
            if ('booleanMatches' in field.fieldType && Object.keys(field.fieldType).length) {
              const booleanMatchKey = Object.keys(field.fieldType.booleanMatches || []).find(
                (key) => key.toLowerCase() === curr?.toLowerCase()
              )!;
              const booleanMatch = field.fieldType.booleanMatches?.[booleanMatchKey];
              acc[column.value] = booleanMatchKey ? booleanMatch : normalizeCheckboxValue(curr);
            } else {
              acc[column.value] = normalizeCheckboxValue(curr);
            }
            return acc;
          }
          case ColumnType.matched: {
            acc[column.value] = curr === '' ? undefined : curr;
            return acc;
          }
          case ColumnType.matchedSelect:
          case ColumnType.matchedSelectOptions: {
            const fieldType = fields.find((field) => field.key === column.value)?.fieldType;

            if (fieldType?.type === 'multiselect') {
              const value = normalizeMultiValue(curr, fieldType.valueSeparator);
              if (!value) {
                acc[column.value] = undefined;
              } else {
                const matchedOptions = column.matchedOptions.filter(({ entry }) =>
                  entry ? value.includes(entry) : false
                );
                acc[column.value] = matchedOptions.length
                  ? filterNotEmpty(matchedOptions?.map((option) => option.value))
                  : undefined;
              }
            } else {
              const matchedOption = column.matchedOptions.find(({ entry, value }) => entry === curr);
              acc[column.value] = matchedOption?.value || undefined;
            }
            return acc;
          }
          case ColumnType.empty:
          case ColumnType.ignored: {
            return acc;
          }
          default:
            return acc;
        }
      },
      {} as Data<T>
    )
  );
